import {
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Button,
  Input,
  Modal,
  Typography
} from 'antd';
import type { InputRef } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import {
  Bug,
  Server,
  UsersRound
} from 'lucide-react';
import workspaceService from '../../../service/workspace.service';
import { slugify } from '../../../utils/slugify';

interface DeleteWorkspaceModalProps {
  workspace: any | null;
  open: boolean;
  onCancel: () => void;
  // Called once the backend has confirmed the delete. The caller must refetch the list.
  onDeleted: (workspace: any) => Promise<void> | void;
  // Called when the DELETE came back 404. That is *not* a confirmed delete — see the
  // comment on the 404 branch below — so the caller reconciles against a fresh list
  // rather than being told the workspace is gone.
  onUnavailable: (workspace: any) => Promise<void> | void;
}

export default function DeleteWorkspaceModal({
  workspace,
  open,
  onCancel,
  onDeleted,
  onUnavailable
}: DeleteWorkspaceModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<InputRef>(null);

  // The token is what the user has to type back to confirm. It is purely a UI gate
  // (the delete itself keys off `workspace.id`), so derive a friendly ASCII slug
  // from the name: accented letters transliterate to their base form
  // ("Wafacash Sénégal" → "wafacash-senegal") rather than the backend slug's
  // "wafacash-s-n-gal". Fall back to the backend slug/name if the name is empty.
  const confirmToken =
    slugify(workspace?.name || '') || workspace?.slug || workspace?.name || '';
  const confirmed = confirmText.trim() === confirmToken;

  const hasCounts = ['user_count', 'vuln_count', 'asset_count'].some(
    (key) => typeof workspace?.[key] === 'number'
  );

  useEffect(() => {
    if (open) {
      setConfirmText('');
      setError(null);
      setDeleting(false);
    }
  }, [open, workspace?.id]);

  const onConfirm = async () => {
    // Guard against a double-submit: a second DELETE would come back as a 404.
    if (!workspace || !confirmed || deleting) return;

    setDeleting(true);
    setError(null);

    const res = await workspaceService.deleteWorkspace(workspace.id);

    if (res.success) {
      await onDeleted(workspace);
      return;
    }

    // §4.1: a record outside the caller's reach answers 404, not 403 — so a 404 here is
    // "no workspace with this id that you can reach", which is *either* already deleted
    // *or* still there and no longer yours. This used to be reported as a successful
    // delete; it cannot be, because we cannot tell those apart. Hand it to the caller to
    // reconcile against a fresh list instead of asserting an outcome.
    if (res.status === 404) {
      await onUnavailable(workspace);
      return;
    }

    // 403: the caller isn't a member, or is a member without permission. `can_delete`
    // can go stale between page load and click, so this is reachable. Show the
    // backend's reason inline and leave the modal open.
    setError(res.message || 'Failed to delete this workspace.');
    setDeleting(false);
  };

  return (
    <Modal
      open={open}
      onCancel={deleting ? undefined : onCancel}
      closable={!deleting}
      maskClosable={!deleting}
      // Focus lands here rather than in a useEffect: during the open transition antd's
      // focus trap pulls focus back to the modal and would swallow an earlier call.
      afterOpenChange={(opened) => {
        if (opened) inputRef.current?.focus();
      }}
      centered
      destroyOnClose
      title={
        <div className="flex items-center gap-2">
          <ExclamationCircleFilled className="text-red-500" />
          <span>
            Delete workspace
          </span>
        </div>
      }
      footer={[
        <Button
          key="cancel"
          onClick={onCancel}
          disabled={deleting}
        >
          Cancel
        </Button>, <Button
          key="delete"
          danger
          type="primary"
          loading={deleting}
          disabled={!confirmed || deleting}
          onClick={onConfirm}
        >
          Delete workspace
        </Button>
      ]}
    >
      <div className="flex flex-col gap-4 mt-4">
        <div>
          You are about to permanently delete
          {' '}
          <span className="font-semibold">{workspace?.name}</span>
          .
        </div>

        <Alert
          type="error"
          showIcon
          message="This cannot be undone"
          description={
            <div className="flex flex-col gap-2">
              <div>
                Every asset and vulnerability in this workspace is destroyed, along with its
                tags, evidence, comments, reports, projects, applications, connectors
                (including their stored credentials), SLA policies, risk configuration and
                all workspace memberships.
              </div>
              <div>
                There is no restore.
              </div>
            </div>
          }
        />

        {/* The v2 list payload does not currently carry these counts. Render them only
            when the backend actually sends a number — a hardcoded 0 next to "this is
            irreversible" reads as "there is nothing in here to lose", which is a lie. */}
        {hasCounts && (
          <div className="flex gap-5 text-[#6B7280]">
            {typeof workspace?.user_count === 'number' && (
              <div className="flex items-center gap-1">
                {workspace.user_count}
                <UsersRound className="w-4" />
              </div>
            )}
            {typeof workspace?.vuln_count === 'number' && (
              <div className="flex items-center gap-1">
                {workspace.vuln_count}
                <Bug className="w-4" />
              </div>
            )}
            {typeof workspace?.asset_count === 'number' && (
              <div className="flex items-center gap-1">
                {workspace.asset_count}
                <Server className="w-4" />
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Typography.Text>
            Type
            {' '}
            <span className="font-semibold">{confirmToken}</span>
            {' '}
            to confirm.
          </Typography.Text>
          <Input
            ref={inputRef}
            value={confirmText}
            disabled={deleting}
            placeholder={confirmToken}
            autoComplete="off"
            onChange={(e) => setConfirmText(e.target.value)}
            onPressEnter={onConfirm}
          />
        </div>

        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
          />
        )}
      </div>
    </Modal>
  );
}
